import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiHome, FiLock, FiLogOut, FiMenu, FiX } from 'react-icons/fi';
import { endSession } from '../../lib/session';
import { GROUPS, SECTIONS, rightsOf, useSessionUser } from '../../lib/permissions';
import Logo from './Logo';

export const HOME_PATH = '/role';

// A section stays highlighted on its sub-pages (/patients/123 → Dossiers patients).
const isActive = (pathname, path) => {
  const current = pathname.toLowerCase();
  const target = path.toLowerCase();
  return current === target || current.startsWith(`${target}/`);
};

const initials = (name = '') =>
  name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

const NavItem = ({ to, title, icon: Icon, tint, locked, active }) => (
  <Link
    to={to}
    aria-current={active ? 'page' : undefined}
    className={`group flex h-10 items-center gap-[12px] rounded-xl px-[10px] text-[14px] font-medium no-underline transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
      active ? 'bg-black/[0.06] text-ink hover:text-ink' : 'text-black/65 hover:bg-black/[0.04] hover:text-ink'
    }`}
  >
    <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-lg text-white ${tint}`}>
      <Icon size={15} aria-hidden="true" />
    </span>
    <span className="min-w-0 flex-1 truncate">{title}</span>
    {locked && <FiLock size={13} aria-label="Code d’accès demandé" className="flex-none text-black/30" />}
  </Link>
);

const Navigation = ({ rights, pathname }) => (
  <nav aria-label="Navigation principale" className="flex flex-col gap-1">
    <NavItem to={HOME_PATH} title="Tableau de bord" icon={FiHome} tint="bg-ink" active={isActive(pathname, HOME_PATH)} />
    {GROUPS.map((group) => {
      const items = SECTIONS.filter((section) => section.group === group && section.menu !== false && rights[section.id]);
      if (!items.length) return null;
      return (
        <div key={group} className="mt-[18px] flex flex-col gap-1">
          <p className="mb-1 mt-0 px-[10px] text-[11px] font-semibold uppercase tracking-[0.08em] text-black/35">{group}</p>
          {items.map((section) => (
            <NavItem
              key={section.id}
              to={section.path}
              title={section.title}
              icon={section.icon}
              tint={section.tint}
              locked={section.locked}
              active={isActive(pathname, section.path)}
            />
          ))}
        </div>
      );
    })}
  </nav>
);

const Brand = () => (
  <div className="flex items-center gap-[10px]">
    <Logo size={36} />
    <span className="leading-tight">
      <span className="block text-[15px] font-semibold tracking-tight text-ink">CTCV · CUOMO</span>
      <span className="block text-[12px] text-black/45">Hôpital Fann</span>
    </span>
  </div>
);

const UserCard = ({ user }) => (
  <div className="flex items-center gap-[10px] rounded-2xl bg-black/[0.03] px-[10px] py-[10px]">
    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-white text-[13px] font-semibold text-ink ring-1 ring-black/[0.06]">
      {initials(user.name)}
    </span>
    <span className="min-w-0 flex-1 leading-tight">
      <span className="block truncate text-[13.5px] font-semibold text-ink">{user.name}</span>
      <span className="block truncate text-[12px] text-black/45">
        {[user.role, user.service].filter(Boolean).join(' · ')}
      </span>
    </span>
    <button
      type="button"
      onClick={() => endSession()}
      aria-label="Se déconnecter"
      title="Se déconnecter"
      className="flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] text-black/45 transition hover:bg-black/[0.06] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
    >
      <FiLogOut size={15} aria-hidden="true" />
    </button>
  </div>
);

// Frame of every screen of the new platform: side menu (drawer on phones) listing only
// the sections the account may open.
const AppShell = ({ children }) => {
  const { pathname } = useLocation();
  const user = useSessionUser();
  const rights = rightsOf(user);
  const [menuOpen, setMenuOpen] = useState(false);

  // A new screen starts at the top, not at the scroll position of the previous one.
  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <div className="min-h-screen bg-mist font-sans">
      {/* Desktop side menu */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] flex-col border-0 border-r-[1px] border-solid border-black/[0.06] bg-white/80 backdrop-blur-xl lg:flex print:!hidden">
        <div className="px-[20px] pb-2 pt-[20px]">
          <Brand />
        </div>
        <div className="flex-1 overflow-y-auto px-[12px] py-[12px]">
          <Navigation rights={rights} pathname={pathname} />
        </div>
        <div className="px-[12px] pb-[12px]">
          <UserCard user={user} />
        </div>
      </aside>

      {/* Phone and tablet bar */}
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-0 border-b-[1px] border-solid border-black/[0.06] bg-white/80 px-[12px] backdrop-blur-xl lg:hidden print:!hidden">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Ouvrir le menu"
          aria-expanded={menuOpen}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] text-ink transition hover:bg-black/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
        >
          <FiMenu size={20} aria-hidden="true" />
        </button>
        <Brand />
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.05] text-[12.5px] font-semibold text-ink">
          {initials(user.name)}
        </span>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={() => setMenuOpen(false)} aria-hidden="true" />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            className="absolute inset-y-0 left-0 flex w-[292px] max-w-[86vw] flex-col bg-white shadow-[0_24px_64px_rgba(0,0,0,0.25)]"
          >
            <div className="flex items-center justify-between px-[16px] pb-2 pt-[16px]">
              <Brand />
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Fermer le menu"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-0 bg-black/[0.05] text-ink transition hover:bg-black/[0.09]"
              >
                <FiX size={17} aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-[12px] py-[12px]">
              <Navigation rights={rights} pathname={pathname} />
            </div>
            <div className="px-[12px] pb-[16px]">
              <UserCard user={user} />
            </div>
          </div>
        </div>
      )}

      <div className="lg:pl-[264px] print:!pl-0">{children}</div>
    </div>
  );
};

export default AppShell;
